import { useState, type FormEvent } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/contexts/I18nContext";
import { useToast } from "@/contexts/ToastContext";
import { Button } from "@/components/Button";
import { TextField } from "@/components/Field";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

/**
 * Sign in.
 *
 * One field for the identifier, because there is one thing printed on the slip
 * and a teacher should not have to decide which kind of thing it is. An `@`
 * decides; a username is resolved by the backend first.
 *
 * The failure message is the same for a wrong password, an unknown account and
 * a disabled one. Distinguishing them tells whoever is probing the form which
 * accounts exist, and helps the person who mistyped not at all.
 *
 * The wordmark sits above the card rather than inside it, and the card's own
 * heading is the name of the task. A studio owner opening this link for the
 * first time is not asking what the product is called — it is on the page they
 * clicked from — they are asking what to do here, and "Sign in" answers that
 * in the one place a screen reader reads first.
 */
export function LoginPage() {
  const t = useT();
  const { status, signIn, requestPasswordReset } = useAuth();
  const { showError, success, info } = useToast();
  const location = useLocation();

  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [resetting, setResetting] = useState(false);

  if (status === "signedIn") {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from && from !== "/login" ? from : "/"} replace />;
  }

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      await signIn(identifier, password);
      // The auth listener flips `status`, and the redirect above runs.
      // Navigating here as well would race it.
    } catch (error) {
      showError(error);
    } finally {
      setBusy(false);
    }
  };

  const onReset = async () => {
    setResetting(true);
    try {
      const outcome = await requestPasswordReset(identifier);
      if (outcome === "sent") success("auth.resetLinkSent");
      else info("auth.resetNeedsEmail");
    } catch (error) {
      showError(error);
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-canvas">
      <div className="flex items-center justify-between px-6 py-4">
        {/* The wordmark: the one place the display serif survives in Backstage. */}
        <p className="font-display text-xl text-ink">{t("app.name")}</p>
        <LanguageSwitcher />
      </div>

      <main className="flex flex-1 items-start justify-center px-4 pb-24 pt-6">
        <div className="card w-full max-w-sm p-8">
          <h1 className="page-title">{t("auth.signIn")}</h1>

          <form className="mt-8 flex flex-col gap-4" onSubmit={onSubmit}>
            <TextField
              labelKey="auth.identifier"
              hintKey="auth.identifierHint"
              type="text"
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              autoFocus
              required
              value={identifier}
              onChange={(event) => setIdentifier(event.target.value)}
            />
            <TextField
              labelKey="auth.password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            <Button type="submit" busy={busy} className="mt-2 w-full">
              {busy ? t("auth.signingIn") : t("auth.signIn")}
            </Button>
          </form>

          <details className="mt-6 text-[14px]">
            <summary className="cursor-pointer rounded-control text-ink-muted hover:text-ink">
              {t("auth.forgotPassword")}
            </summary>
            <p className="mt-2 text-ink-muted">{t("auth.forgotPasswordHelp")}</p>
            <Button
              variant="secondary"
              busy={resetting}
              onClick={onReset}
              className="mt-3 w-full"
            >
              {t("auth.sendResetLink")}
            </Button>
          </details>

          <p className="mt-6 text-[12px] text-ink-faint">{t("auth.sameAccount")}</p>
        </div>
      </main>
    </div>
  );
}
